import React from 'react';

interface NestPalette {
  bg: string;
  ink: string;
  accent1: string;
  accent2: string;
}

interface NestLanguage {
  code: string;
  native: string;
}

interface NestIllustrationProps {
  palette: NestPalette;
  languages: NestLanguage[];
  className?: string;
}

// CJK ideographs, Hiragana/Katakana, and Hangul render at roughly 1em per
// character at the bubble's Nunito 15px size, while Latin/Cyrillic average
// around 0.55em. A single regex bump per string keeps the math local and
// avoids per-glyph segmentation.
// CJK ranges: Hiragana/Katakana, CJK Ext A, CJK Unified, Hangul.
// Does not cover CJK punctuation or fullwidth Latin; fine for current language list.
const CJK_GLYPH_RE = /[぀-ヿ㐀-䶿一-鿿가-힯]/;
const CJK_CHAR_WIDTH = 14;
const LATIN_CHAR_WIDTH = 8.5;
const BUBBLE_PADDING = 28;
const MIN_BUBBLE_WIDTH = 80;

export function approximateBubbleWidth(native: string): number {
  const perChar = CJK_GLYPH_RE.test(native) ? CJK_CHAR_WIDTH : LATIN_CHAR_WIDTH;
  const raw = native.length * perChar + BUBBLE_PADDING;
  return Math.max(MIN_BUBBLE_WIDTH, Math.round(raw));
}

const NestIllustration: React.FC<NestIllustrationProps> = ({
  palette,
  languages,
  className = '',
}) => {
  const { bg, ink, accent1, accent2 } = palette;
  const picks = languages.slice(0, 6);
  return (
    <svg
      viewBox="0 0 600 520"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Abstract nest with language bubbles"
    >
      <rect width="600" height="520" fill={bg} />
      {/* Nest arcs */}
      <g transform="translate(300 340)">
        {[220, 180, 140, 100, 60].map((r, i) => (
          <circle
            key={r}
            r={r}
            fill="none"
            stroke={i % 2 ? accent1 : ink}
            strokeOpacity={0.12 + i * 0.08}
            strokeWidth={i === 0 ? 2 : 1.5}
          />
        ))}
        <circle r="28" fill={accent2} />
      </g>
      {/* Floating language bubbles */}
      {picks.map((lng, i) => {
        const cx = 80 + (i % 3) * 180 + (i > 2 ? 20 : 0);
        const cy = 80 + Math.floor(i / 3) * 120;
        const fill = i % 2 ? accent1 : accent2;
        const width = approximateBubbleWidth(lng.native);
        const x = -width / 2;
        return (
          <g key={lng.code} transform={`translate(${cx} ${cy})`}>
            <rect x={x} y="-22" width={width} height="44" rx="22" fill={fill} opacity={0.92} />
            <text
              x="0"
              y="5"
              textAnchor="middle"
              fontFamily="Nunito, system-ui"
              fontWeight="700"
              fontSize="15"
              fill={ink}
            >
              {lng.native}
            </text>
          </g>
        );
      })}
      {/* Orbit line */}
      <path
        d="M 40 360 Q 300 200 560 360"
        fill="none"
        stroke={ink}
        strokeOpacity="0.15"
        strokeWidth="1.2"
        strokeDasharray="4 6"
      />
    </svg>
  );
};

export default NestIllustration;
